import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { collection, addDoc, getDocs, query, where } from "firebase/firestore";
import axios from "axios";
import { UserCheck, ListVideo, AlertTriangle, Plus, Music4 } from 'lucide-react';

import CustomAlert from "../components/CustomAlert";
import ConfirmationModal from "../components/ConfirmationModal";
import removeCredit from "../constants/RemoveCredit";
import getUser from "../constants/getUser";
import Navbar from "../components/Navbar";
import Footerbar from "../components/Footerbar";
import { fetchSearchResults } from "../constants/api/youtube";
import { initDynamicFirebase } from "../constants/initDynamicFirebase";
import { clearActiveRoomId } from '../lib/activeRoomSession';

if (!document.getElementById('spinner-keyframes')) {
    const keyframes = `@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`;
    const styleSheet = document.createElement("style");
    styleSheet.id = 'spinner-keyframes';
    styleSheet.type = "text/css";
    styleSheet.innerText = keyframes;
    document.head.appendChild(styleSheet);
}

const API_BASE_URL = 'https://apem.riderup.uk/api';
const getAuthHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token') || ''}` });
const getVideoId = (video) => video?.videoId || video?.video_id || video?.id || '';
const getVideoThumbnail = (video) => {
    const thumbnail = video?.thumbnail;
    if (typeof thumbnail === 'string') return thumbnail;
    return thumbnail?.[0]?.url || thumbnail?.url || video?.thumbnails?.[0]?.url || '';
};
const getCurrentCoordinates = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
        reject(new Error('Este dispositivo não oferece acesso à localização.'));
        return;
    }

    navigator.geolocation.getCurrentPosition(
        ({ coords }) => resolve({ latitude: coords.latitude, longitude: coords.longitude }),
        (positionError) => reject(new Error(positionError.code === 1
            ? 'Permita o acesso à localização para pedir músicas na sala.'
            : 'Não foi possível obter sua localização. Verifique o GPS e tente novamente.')),
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 5000 }
    );
});

const ResultScreen = () => {
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [db, setDb] = useState(null);
    const [loadingEstablishment, setLoadingEstablishment] = useState(true);
    const [addingVideoId, setAddingVideoId] = useState(null);
    const [alertInfo, setAlertInfo] = useState({ show: false, message: "", type: "error" });
    const [confirmState, setConfirmState] = useState({ show: false, message: "", videoToConfirm: null });
    const [connectionModal, setConnectionModal] = useState({ show: false, message: "" });
    const [oferecimento, setOferecimento] = useState(null);
    const [activeRoomId, setActiveRoomId] = useState('');

    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    const searchQuery = queryParams.get("query") || "";
    const selectedVideo = location.state?.selectedVideo || null;
    const autoConfirmVideoRef = useRef(null);

    useEffect(() => {
        let isMounted = true;
        const loadFirebase = async () => {
            setDb(null);
            setLoadingEstablishment(true);
            setError(null);
            try {
                const user = await getUser();
                if (!user) {
                    if (isMounted) navigate('/');
                    return;
                }

                const savedRoomId = localStorage.getItem('activeRoomId');
                if (savedRoomId) {
                    try {
                        const roomResponse = await axios.get(`${API_BASE_URL}/rooms/${savedRoomId}`, { headers: getAuthHeaders() });
                        if (isMounted && roomResponse.data?.room?.id) {
                            setActiveRoomId(String(roomResponse.data.room.id));
                            return;
                        }
                    } catch (roomError) {
                        if ([401, 403, 404, 410].includes(roomError.response?.status)) {
                            clearActiveRoomId();
                        } else {
                            throw roomError;
                        }
                    }
                }

                if (!user.client) {
                    if (isMounted) {
                        setConnectionModal({
                            show: true,
                            message: "Conecte-se a um estabelecimento ou entre em uma sala para buscar e pedir músicas.",
                        });
                        setLoading(false);
                        setLoadingEstablishment(false);
                    }
                    return;
                }

                try {
                    const sponsorResponse = await axios.get(`${API_BASE_URL}/oferecimentos/active/${user.client_id}`);
                    if (isMounted) {
                        setOferecimento(sponsorResponse?.data?.data || null);
                    }
                } catch (sponsorError) {
                    if (isMounted) {
                        setOferecimento(null);
                    }
                }

                const config = {
                    apiKey: "AIzaSyBMVTvCciOYjyTFES0wapwkiVxTRL_9WJU",
                    authDomain: "apemall.firebaseapp.com",
                    projectId: "apemall",
                    storageBucket: "apemall.firebasestorage.app",
                    messagingSenderId: "824620532506",
                    appId: "1:824620532506:web:1378444a5c52e09807e2a3",
                    measurementId: "G-1YDCLNVCKH"
                };
                if (!config.apiKey || !config.projectId) throw new Error("Configuração do Firebase incompleta ou inválida.");
                const firebaseDb = await initDynamicFirebase(config);
                if (isMounted) setDb(firebaseDb);
            } catch (err) {
                if (isMounted) {
                    setError(`Erro ao carregar configuracao do estabelecimento: ${err.message}`);
                    setLoading(false);
                }
            } finally {
                if (isMounted) {
                    setLoadingEstablishment(false);
                }
            }
        };
        loadFirebase();
        return () => {
            isMounted = false;
        };
    }, [navigate]);

    const fetchData = useCallback(async (queryTerm) => {
        if (!queryTerm) return;
        setLoading(true);
        setError(null);
        setVideos([]);
        try {
            if (activeRoomId) {
                const coordinates = await getCurrentCoordinates();
                const response = await axios.get(`${API_BASE_URL}/rooms/${activeRoomId}/search`, {
                    params: { query: queryTerm.trim(), ...coordinates },
                    headers: getAuthHeaders(),
                });
                setVideos(Array.isArray(response.data) ? response.data : []);
                return;
            }

            const user = await getUser();
            if (!user?.client_id) {
                setConnectionModal({
                    show: true,
                    message: "Conecte-se a um estabelecimento ou entre em uma sala para buscar e pedir músicas.",
                });
                return;
            }

            const data = await fetchSearchResults(queryTerm, user.client_id, 'youtube');
            setVideos(data || []);
        } catch (fetchError) {
            setError(fetchError.response?.data?.message || fetchError.message || "Erro ao buscar vídeos. Verifique sua conexão.");
            setVideos([]);
        } finally {
            if (document.getElementById('result-screen-root')) setLoading(false);
        }
    }, [activeRoomId]);

    useEffect(() => {
        if (selectedVideo) {
            setVideos([selectedVideo]);
            return;
        }

        if (searchQuery && !loadingEstablishment) {
            fetchData(searchQuery);
        } else if (!searchQuery) {
            navigate(-1);
        }
    }, [searchQuery, selectedVideo, fetchData, navigate, loadingEstablishment]);

    const handleVideoClick = useCallback((video) => {
        if (!db && !activeRoomId) {
            setAlertInfo({
                show: true,
                message: "Aguarde sua conexão carregar antes de pedir a música.",
                type: "error",
            });
            return;
        }
        if (addingVideoId) return;
        setConfirmState({ show: true, message: `Pedir a música "${video.title || 'esta música'}"?`, videoToConfirm: video });
    }, [addingVideoId, activeRoomId, db]);

    useEffect(() => {
        if (!selectedVideo || (!db && !activeRoomId)) return;
        const videoId = getVideoId(selectedVideo);
        if (autoConfirmVideoRef.current === videoId) return;

        autoConfirmVideoRef.current = videoId;
        handleVideoClick(selectedVideo);
    }, [selectedVideo, db, activeRoomId, handleVideoClick]);

    const executeAddVideo = async () => {
        const video = confirmState.videoToConfirm;
        if (!video) return;
        const videoId = getVideoId(video);
        if (!videoId) {
            setConfirmState({ show: false, message: "", videoToConfirm: null });
            setAlertInfo({ show: true, message: "Não foi possível identificar essa música.", type: 'error' });
            return;
        }

        setConfirmState({ show: false, message: "", videoToConfirm: null });
        setAddingVideoId(videoId);
        setError(null);

        try {
            if (activeRoomId) {
                const coordinates = await getCurrentCoordinates();
                await axios.post(`${API_BASE_URL}/rooms/${activeRoomId}/requests`, {
                    video_id: videoId,
                    ...coordinates,
                }, { headers: getAuthHeaders() });
                setAlertInfo({ show: true, message: "Música enviada para a fila da sala!", type: 'success' });
                return;
            }

            const user = await getUser();
            if (!user) throw new Error("Não foi possível obter informações do usuário. Faça login novamente.");
            if (!user.client) {
                setConnectionModal({
                    show: true,
                    message: "Conecte-se a um estabelecimento ou entre em uma sala para pedir músicas.",
                });
                return;
            }

            const creditResponse = await removeCredit(videoId);
            if (!creditResponse?.success) {
                throw new Error(creditResponse?.error || "Erro desconhecido ao debitar crédito.");
            }

            const empresaId = String(user.client_id);
            const videosRef = collection(db, 'empresa', empresaId, 'lista');
            const q = query(videosRef, where("id", "==", videoId));
            const querySnapshot = await getDocs(q);

            if (!querySnapshot.empty) {
                throw new Error("Esta música já está na fila ou tocou recentemente.");
            }

            const docToAdd = {
                author: user.name ? user.name.toUpperCase() : 'USUÁRIO APP',
                date: '',
                user_id: user.id,
                fecha: Date.now(),
                id: videoId,
                id_ponto: user.client?.id || 1,
                img: getVideoThumbnail(video) || `https://i.ytimg.com/vi/${videoId}/mqdefault.jpg`,
                title: video.title || 'Título Desconhecido',
            };

            await addDoc(videosRef, docToAdd);
            setAlertInfo({ show: true, message: "Música adicionada com sucesso!", type: 'success' });
        } catch (e) {
            setAlertInfo({ show: true, message: `Erro: ${e.message || "Não foi possível completar a operação."}`, type: 'error' });
        } finally {
            if (document.getElementById('result-screen-root')) setAddingVideoId(null);
        }
    };

    const cancelAddVideo = () => setConfirmState({ show: false, message: "", videoToConfirm: null });
    const handleCloseAlert = () => setAlertInfo({ ...alertInfo, show: false });
    const handleConnectToEstablishment = () => {
        setConnectionModal({ show: false, message: "" });
        navigate('/bars');
    };
    const handleConnectToRoom = () => {
        setConnectionModal({ show: false, message: "" });
        navigate('/manage-room');
    };

    return (
        <div id="result-screen-root" style={styles.pageContainer}>
            {alertInfo.show && <CustomAlert message={alertInfo.message} type={alertInfo.type} onclose={handleCloseAlert} />}
            <ConfirmationModal show={confirmState.show} message={confirmState.message} onConfirm={executeAddVideo} onCancel={cancelAddVideo} />
            {connectionModal.show && (
                <div style={styles.modalOverlay}>
                    <div style={styles.modalBox}>
                        <AlertTriangle size={46} color="#e7c651" />
                        <h3 style={styles.modalTitle}>Conecte-se para pedir música</h3>
                        <p style={styles.modalMessage}>{connectionModal.message}</p>
                        <button onClick={handleConnectToEstablishment} style={styles.modalButton}>
                            Ver estabelecimentos
                        </button>
                        <button onClick={handleConnectToRoom} style={styles.modalSecondaryButton}>Criar ou entrar em sala</button>
                    </div>
                </div>
            )}
            {addingVideoId && !activeRoomId && (
                <div style={styles.loadingOverlay}>
                    <div style={styles.loadingCard}>
                        <div style={styles.loadingTag}>Oferecimento</div>
                        <h2 style={styles.loadingTitle}>{oferecimento?.titulo || 'Esta música é um oferecimento'}</h2>
                        <p style={styles.loadingSubtitle}>{oferecimento?.subtitulo || 'Aguarde enquanto seu pedido está sendo enviado.'}</p>

                        <div style={styles.sponsorImageWrap}>
                            {oferecimento?.imagem ? (
                                <img
                                    src={oferecimento.imagem}
                                    alt={oferecimento?.titulo || 'Oferecimento'}
                                    style={styles.sponsorImage}
                                    onError={(e) => {
                                        e.currentTarget.style.display = 'none';
                                    }}
                                />
                            ) : (
                                <div style={styles.sponsorPlaceholder}>
                                    <Music4 size={48} color="#ffffff" />
                                </div>
                            )}
                        </div>

                        <div style={styles.loadingRow}>
                            <div style={styles.spinner}></div>
                            <span style={styles.loadingText}>Adicionando sua música...</span>
                        </div>
                    </div>
                </div>
            )}
            <Navbar title="Resultados da Busca" showLogo={false} />
            <div style={styles.contentArea}>
                {loading && videos.length === 0 && (
                    <div style={styles.centeredMessage}>
                        <div style={styles.spinner}></div>
                        <p>Buscando vídeos...</p>
                    </div>
                )}
                {!loading && error && (
                    <div style={styles.centeredMessage}>
                        <AlertTriangle size={40} color="#ff6060" />
                        <p style={styles.errorText}>{error}</p>
                        {error.includes("buscar vídeos") && (
                            <button onClick={() => fetchData(searchQuery)} style={styles.retryButton}>Tentar Novamente</button>
                        )}
                    </div>
                )}
                {!loading && !error && videos.length === 0 && searchQuery && (
                    <div style={styles.centeredMessage}>
                        <ListVideo size={40} color="#a0a0b0" />
                        <p>Nenhum resultado encontrado para "{searchQuery}".</p>
                        <p style={{ fontSize: '14px', color: '#a0a0b0' }}>Tente buscar por outros termos.</p>
                    </div>
                )}
                {!error && videos.length > 0 && (
                    <div style={styles.resultsList}>
                        {videos.map((video) => {
                            const videoId = getVideoId(video);
                            return <div key={videoId || video.title} style={styles.videoCard}>
                                <img
                                    src={getVideoThumbnail(video) || `https://i.ytimg.com/vi/${videoId}/mqdefault.jpg`}
                                    alt={`Thumbnail for ${video.title}`}
                                    style={styles.thumbnail}
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                    }}
                                />
                                <div style={styles.videoInfo}>
                                    <h3 style={styles.videoTitle}>{video.title || 'Título Indisponível'}</h3>
                                    <p style={styles.videoArtist}>
                                        <UserCheck size={12} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                                        {video.channelTitle || 'Canal Desconhecido'}
                                    </p>
                                </div>
                                <button
                                    style={styles.addButton(addingVideoId === videoId || loadingEstablishment)}
                                    onClick={() => handleVideoClick(video)}
                                    disabled={!!addingVideoId || loadingEstablishment}
                                >
                                    {addingVideoId === videoId ? (
                                        <>
                                            <div style={styles.buttonSpinner}></div> Adicionando...
                                        </>
                                    ) : loadingEstablishment ? (
                                        <>
                                            <div style={styles.buttonSpinner}></div> Carregando...
                                        </>
                                    ) : (
                                        <>
                                            <Plus size={16} /> Pedir
                                        </>
                                    )}
                                </button>
                            </div>;
                        })}
                    </div>
                )}
            </div>
            <Footerbar />
        </div>
    );
};

const styles = {
    pageContainer: { display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#12121f', color: '#f0f0f0', overflowX: 'hidden', boxSizing: 'border-box' },
    contentArea: { flexGrow: 1, paddingTop: '70px', paddingBottom: '75px', paddingLeft: '10px', paddingRight: '10px', overflowY: 'auto', boxSizing: 'border-box', width: '100%', maxWidth: '600px', marginLeft: 'auto', marginRight: 'auto' },
    centeredMessage: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '40px 20px', color: '#a0a0b0', minHeight: 'calc(100vh - 135px)', boxSizing: 'border-box' },
    errorText: { color: '#ff6060', marginTop: '10px', maxWidth: '80%', lineHeight: '1.4', overflowWrap: 'break-word', wordWrap: 'break-word' },
    retryButton: { marginTop: '20px', padding: '10px 20px', backgroundColor: '#6a0dad', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold' },
    resultsList: {},
    videoCard: { display: "flex", flexDirection: "column", marginBottom: "15px", backgroundColor: "#1a1a2e", borderRadius: "8px", textAlign: "left", boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)', overflow: 'hidden', border: '1px solid #2a2a4e' },
    thumbnail: { width: "100%", aspectRatio: '16 / 9', objectFit: 'cover', display: 'block', backgroundColor: '#333' },
    videoInfo: { width: "100%", padding: "10px 12px", boxSizing: 'border-box' },
    videoTitle: { fontSize: "14px", fontWeight: "600", color: "#ffffff", marginBottom: "3px", lineHeight: '1.3', overflowWrap: 'break-word', wordWrap: 'break-word', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis' },
    videoArtist: { fontSize: "12px", color: "#a0a0b0", fontWeight: "500", display: 'flex', alignItems: 'center', overflowWrap: 'break-word', wordWrap: 'break-word', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    addButton: (isLoading) => ({ backgroundColor: isLoading ? '#555' : '#6a0dad', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: "8px 10px", width: "100%", borderBottomLeftRadius: '7px', borderBottomRightRadius: '7px', color: isLoading ? '#aaa' : "#FFF", fontSize: "13px", fontWeight: "bold", border: "none", cursor: isLoading ? "not-allowed" : "pointer", marginTop: "0", transition: 'background-color 0.3s ease, color 0.3s ease, opacity 0.3s ease', opacity: isLoading ? 0.7 : 1, borderTop: `1px solid ${isLoading ? '#666' : '#7a1cc2'}`, boxSizing: 'border-box', height: '40px' }),
    spinner: { border: '4px solid rgba(255, 255, 255, 0.3)', borderLeftColor: '#a0a0ff', borderRadius: '50%', width: '30px', height: '30px', animation: 'spin 1s linear infinite', marginBottom: '15px' },
    buttonSpinner: { border: '3px solid rgba(255, 255, 255, 0.3)', borderLeftColor: '#fff', borderRadius: '50%', width: '14px', height: '14px', animation: 'spin 1s linear infinite', display: 'inline-block' },
    loadingOverlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(8, 8, 20, 0.92)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' },
    loadingCard: { width: '100%', maxWidth: '420px', borderRadius: '22px', background: 'linear-gradient(180deg, #1d1731 0%, #120f22 100%)', border: '1px solid rgba(145, 98, 255, 0.35)', boxShadow: '0 20px 60px rgba(0,0,0,0.45)', padding: '24px', textAlign: 'center' },
    loadingTag: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '6px 12px', borderRadius: '999px', backgroundColor: 'rgba(148, 88, 255, 0.18)', color: '#cfb6ff', fontSize: '12px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '14px' },
    loadingTitle: { fontSize: '24px', lineHeight: 1.2, color: '#ffffff', margin: '0 0 8px 0' },
    loadingSubtitle: { fontSize: '14px', lineHeight: 1.5, color: '#b8b2cc', margin: '0 0 18px 0' },
    sponsorImageWrap: {
        width: '100%',
        maxWidth: '320px',
        height: '320px',
        margin: '0 auto 18px auto',
        borderRadius: '18px',
        overflow: 'hidden',
        backgroundColor: '#221b38',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid rgba(255,255,255,0.06)'
    },
    sponsorImage: {
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        display: 'block'
    },
    sponsorPlaceholder: {
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #6a0dad 0%, #2d135c 100%)'
    },
    loadingRow: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', color: '#ffffff', fontWeight: 600 },
    loadingText: { fontSize: '15px' },
    modalOverlay: {
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        backgroundColor: 'rgba(8, 8, 20, 0.86)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
    },
    modalBox: {
        width: '100%',
        maxWidth: '360px',
        borderRadius: '18px',
        backgroundColor: '#1a1a2e',
        border: '1px solid #4a4a6e',
        boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
        padding: '24px',
        textAlign: 'center',
        color: '#f0f0f0',
    },
    modalTitle: {
        margin: '14px 0 8px 0',
        fontSize: '20px',
        lineHeight: 1.2,
    },
    modalMessage: {
        margin: '0 0 20px 0',
        color: '#b8b2cc',
        fontSize: '15px',
        lineHeight: 1.45,
    },
    modalButton: {
        width: '100%',
        border: 'none',
        borderRadius: '10px',
        backgroundColor: '#6a0dad',
        color: '#ffffff',
        padding: '13px 16px',
        fontSize: '16px',
        fontWeight: 700,
        cursor: 'pointer',
    },
    modalSecondaryButton: {
        width: '100%',
        border: '1px solid #625e7e',
        borderRadius: '10px',
        backgroundColor: 'transparent',
        color: '#f0f0f0',
        padding: '12px 16px',
        fontSize: '15px',
        fontWeight: 600,
        cursor: 'pointer',
        marginTop: '10px',
    },
};

export default ResultScreen;
